import type { TFunction, TOptions } from "i18next";
import type {
  ArtDetailSerializer,
  ArtShortSerializer,
  CartArtSerializer,
  MyArtShortSerializer,
  ShortUserSerializerWithCustomSocials,
} from "@litres-libs/types";
import { getPrice, hasWindow } from "./";

interface getHrefForReadingParams {
  art:
    | ArtDetailSerializer
    | ArtShortSerializer
    | CartArtSerializer
    | MyArtShortSerializer;
  user: ShortUserSerializerWithCustomSocials;
  isLibrary?: boolean;
  isBookCard?: boolean;
  isAvailableByAnySubscriptions?: boolean;
  translator?: TFunction;
  lang: string;
  webtoonChapterId?: number;
  webtoonChapterFileId?: number;
}

export const getHrefForReading = (params: getHrefForReadingParams): string => {
  const {
    art,
    user,
    isLibrary,
    isBookCard,
    isAvailableByAnySubscriptions,
    translator,
    lang = "ru",
    webtoonChapterId,
    webtoonChapterFileId,
  } = params;
  const userID = user.id;
  const isLibrarian = user.library_info.is_librarian;
  const isSchoolUser = user.library_info.is_school_domain;
  const isLibUser = user.library_info.is_library_user;
  const hasLitresSubscription = user.subscription.is_active;
  const hasPartnerSubscription = user.partner_subscriptions.has_active;
  const {
    art_type,
    my_art_status,
    availability,
    url,
    is_free,
    library_information,
    title,
    id,
    in_gifts,
    prices,
    can_be_preordered,
    persons,
    uuid,
    is_draft,
    is_draft_full_free,
    release_file_id,
    cover_url,
    is_subscription_art,
    is_available_with_subscription,
    art_subscription_status_for_user,
  } = art;

  const isTextArt = [0, 4].includes(Number(art_type));
  const isAudioArt = [1, 22, 23].includes(Number(art_type));

  if (isAudioArt) {
    return url || "/";
  }

  // Анрег для текстовой книги: отправляем на страницу книги и просим залогиниться
  if (userID === 0 && isTextArt && url && !isBookCard) {
    return `${url}#read_unreg`;
  }

  const translateText = (
    value: string,
    fallbackValue?: string,
    options?: TOptions
  ) => (translator ? translator(value, options || {}) : fallbackValue || value);

  const getFriendlyUrl = () => {
    const author = persons?.filter(person => person.role === "author")[0];
    return encodeURIComponent(
      `${author?.full_name || persons?.[0]?.full_name || ""}. ${title}`
    );
  };

  const epubSupport =
    hasWindow && window.location.href.indexOf("epubSupport") > 0;

  // Арт доступен и есть хотя бы одна из подписок: внутренняя или операторская
  const isSubscriptionArt =
    (!(art.is_free || art.my_art_status > 0) &&
      availability === 1 &&
      ((hasLitresSubscription &&
        (is_subscription_art || is_available_with_subscription)) ||
        (hasPartnerSubscription &&
          art_subscription_status_for_user === "active_subscription"))) ||
    isAvailableByAnySubscriptions;

  // TODO: fb3_master
  const orLink =
    epubSupport || Number(art_type) === 24 ? "or4_adapters" : "or4";

  const isFragment =
    (my_art_status !== 1 || (can_be_preordered && availability !== 1)) &&
    !isSubscriptionArt;

  const isFullVersionAvailableNotBySubscription =
    !isFragment && !isSubscriptionArt;

  // TODO: fb3_master
  const hasEpubSupport = epubSupport || isFullVersionAvailableNotBySubscription;

  const isPdfArt = Number(art_type) === 4;

  if (isPdfArt) {
    let fileIdCase = "";

    const isArtAvailableByPpd = my_art_status === 1 || is_free;

    const isArtAvailableByLibrary =
      !!library_information &&
      library_information.biblio_selfservice === "received";

    if (isArtAvailableByPpd || isArtAvailableByLibrary || isSubscriptionArt) {
      fileIdCase = `file=${release_file_id}`;
    }
    return (
      `/static/or3/view/or.html?` +
      [
        "art_type=4",
        fileIdCase,
        // Не понятно почему используется encodeURIComponent два раза, но так нужно. Без этого получается некорректный заголовок в читалке
        `bname=${encodeURIComponent(encodeURIComponent(title))}`,
        `art=${id}`,
        `user=${userID}`,
        `uuid=${uuid}`,
        `cover=${encodeURIComponent(cover_url || "")}`,
        `uilang=${lang}`,
        isFragment && !is_free && "trial=1",
        isSubscriptionArt && "subscription",
      ]
        .filter(e => !!e)
        .join("&")
    );
  }

  const isWetoonArt = Number(art_type) === 25;

  if (isWetoonArt) {
    if (!webtoonChapterFileId || !webtoonChapterId) return "";

    return (
      `/static/or5/view/or.html?` +
      [
        "art_type=25",
        `file=${webtoonChapterFileId}`,
        // Не понятно почему используется encodeURIComponent два раза, но так нужно. Без этого получается некорректный заголовок в читалке
        `bname=${encodeURIComponent(encodeURIComponent(title))}`,
        `art=${id}`,
        `webtoonChapterId=${webtoonChapterId}`,
        `user=${userID}`,
        `uuid=${uuid}`,
        `cover=${encodeURIComponent(cover_url || "")}`,
        `uilang=${lang}`,
      ]
        .filter(e => !!e)
        .join("&")
    );
  }

  // Фрагмент, т.к. книга недоступна
  if (
    !is_free &&
    !is_draft_full_free &&
    !isSubscriptionArt &&
    my_art_status !== 1
  ) {
    const getReadParams = (): string => {
      if (isLibrary || isLibrarian) {
        return "librarian=1";
      }
      // без права покупки с режимом самообслуживания
      if (library_information?.biblio_selfservice === "instant") {
        return "self_user=1";
      }
      // без права покупки без самообслуживания, книга уже запрошена
      if (library_information?.biblio_selfservice === "requested") {
        return "request_user=1";
      }
      // без права покупки без самообслуживания
      if (library_information?.biblio_selfservice === "delayed") {
        return "buser=1";
      }
      // слонораздача
      if (in_gifts && in_gifts > 0) {
        return "in_gifts=1";
      }
      return "";
    };

    const getPriceForLink = (): string => {
      if (isLibrarian) {
        return "";
      }

      if (!(prices?.currency && prices?.cost_with_bonus)) {
        return "";
      }

      return `${getPrice(prices.cost_with_bonus, prices.currency, undefined)}`;
    };
    const getButtonText = (): string => {
      const priceToUrl = getPriceForLink() ?? 0;

      if (isLibrarian) {
        return availability === 1 ? "Выдать книгу читателю" : "Уведомить";
      }

      if (
        isSchoolUser ||
        (isLibUser && library_information?.biblio_selfservice !== "impossible")
      ) {
        if (library_information?.biblio_selfservice === "instant") {
          return "Взять себе";
        }
        if (library_information?.biblio_selfservice === "requested") {
          return "Отменить";
        }
        if (availability && availability > 1) {
          return "Уведомить";
        }
        return "Запросить";
      }

      // слонораздача или абонемент
      if (in_gifts && in_gifts > 0) {
        return "Взять себе";
      }

      if (availability === 1) {
        return is_draft
          ? `Доступ к черновику за ${priceToUrl}`
          : translateText(
              "Купить полную версию за {book_price}",
              `Купить полную версию за ${priceToUrl}`,
              { book_price: priceToUrl }
            );
      }

      // предзаказ
      if (can_be_preordered) {
        return `Сделать предзаказ за ${priceToUrl}`;
      }

      if (availability && availability > 1) {
        return "Уведомить";
      }
      return "";
    };

    return (
      `/static/${orLink}/view/or.html?` +
      [
        `baseurl=/pub/t/${id}.`,
        `art=${id}`,
        "trials=1",
        `user=${userID}`,
        `file=${release_file_id}`,
        getReadParams(),
        [1, 2].includes(my_art_status) && "preorder_buy=1",
        prices?.cost_with_bonus &&
          !isLibrarian &&
          `price=${prices.cost_with_bonus}`,
        `texttrialbutton=${encodeURIComponent(getButtonText())}`,
        `uilang=${lang}`,
        "catalit2",
        "half=1",
        "track_reading",
        `friendly_url=${getFriendlyUrl()}`,
        hasEpubSupport && `fb3_master=${""}`,
        Number(art_type) === 24 && "fb3_master=2",
      ]
        .filter(e => !!e)
        .join("&")
    );
  }

  // Правила для построения ссылки для полностью бесплатных черновиков
  // без цены и ссылка аналогичная фрагменту
  if (is_draft_full_free) {
    return (
      `/static/${orLink}/view/or.html?` +
      [
        `baseurl=/pub/t/${id}.`,
        `art=${id}`,
        `user=${userID}`,
        `file=${release_file_id}`,
        `uilang=${lang}`,
        "catalit2",
        "half=1",
        "track_reading",
        "draft_free=1",
        `friendly_url=${getFriendlyUrl()}`,
        hasEpubSupport && `fb3_master=${""}`,
        Number(art_type) === 24 && "fb3_master=2",
      ]
        .filter(e => !!e)
        .join("&")
    );
  }

  return (
    `/static/${orLink}/view/or.html?` +
    [
      `baseurl=/download_book${
        isSubscriptionArt ? "_subscr" : ""
      }/${id}/${release_file_id}/`,
      `art=${id}`,
      `user=${userID}`,
      `uilang=${lang}`,
      "catalit2",
      "track_reading",
      hasEpubSupport && `fb3_master=${""}`,
      Number(art_type) === 24 && "fb3_master=2",
      isSubscriptionArt && "subscription",
    ]
      .filter(e => !!e)
      .join("&")
  );
};
